import { useState } from 'react';
import { api, ApiError, formatDateTime } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Broadcast } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatusBadge } from '../../ui/components';

export function Broadcasts() {
  const { persona } = useAuth();
  const canBroadcast = persona?.roles.includes('BROADCASTER') ?? false;
  const [showCreate, setShowCreate] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const listQ = useApiQuery<{ items: Broadcast[] }>(canBroadcast ? '/api/v1/notify/broadcasts?limit=100' : null);

  const dispatch = async (b: Broadcast) => {
    setBusy(b.id);
    setActionError(null);
    try {
      await api.post(`/api/v1/notify/broadcasts/${b.id}/dispatch`);
      listQ.reload();
    } catch (e) {
      setActionError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Dispatch failed');
    } finally {
      setBusy(null);
    }
  };

  if (!canBroadcast) {
    return (
      <div>
        <PageHeader title="Broadcast composer" subtitle="Compose and dispatch broadcasts to a set of citizens" />
        <EmptyState title="No access" hint="This screen needs the BROADCASTER role." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Broadcast composer"
        subtitle="Compose and dispatch broadcasts to a set of citizens"
        actions={
          <button className="btn-primary" onClick={() => setShowCreate(true)}>
            + New broadcast
          </button>
        }
      />
      {actionError ? (
        <div className="mb-4">
          <ErrorNote message={actionError} />
        </div>
      ) : null}
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No broadcasts yet" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Template</th>
                <th className="table-head">Audience</th>
                <th className="table-head">Priority</th>
                <th className="table-head">Status</th>
                <th className="table-head">Scheduled</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((b) => (
                <tr key={b.id} className="hover:bg-slate-50">
                  <td className="table-cell font-medium">{b.templateCode}</td>
                  <td className="table-cell tabular-nums">{b.audienceQuery.personIds?.length ?? 0}</td>
                  <td className="table-cell">
                    {b.priority ? <StatusBadge status="WARN" /> : <span className="text-xs text-slate-300">—</span>}
                  </td>
                  <td className="table-cell">
                    <StatusBadge status={b.status} />
                  </td>
                  <td className="table-cell text-slate-500">{b.scheduledAt ? formatDateTime(b.scheduledAt) : '—'}</td>
                  <td className="table-cell text-right">
                    {b.status !== 'DISPATCHED' ? (
                      <button className="btn-primary !px-2.5 !py-1 text-xs" onClick={() => dispatch(b)} disabled={busy === b.id}>
                        {busy === b.id ? 'Dispatching…' : 'Dispatch now'}
                      </button>
                    ) : (
                      <span className="text-[11px] text-slate-400">
                        sent {b.stats.sent ?? 0} · queued {b.stats.queued ?? 0} · failed {b.stats.failed ?? 0}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showCreate ? (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDone={() => {
            setShowCreate(false);
            listQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function CreateModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { persona } = useAuth();
  const canEmergency = persona?.roles.includes('EMERGENCY_BROADCASTER') ?? false;
  const [templateCode, setTemplateCode] = useState('');
  const [personIds, setPersonIds] = useState('');
  const [scheduledAt, setScheduledAt] = useState('');
  const [priority, setPriority] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const ids = personIds
        .split(/[\n,]/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (ids.length === 0) throw new Error('Enter at least one recipient personId/NIN');
      await api.post('/api/v1/notify/broadcasts', {
        templateCode,
        audienceQuery: { personIds: ids },
        ...(scheduledAt ? { scheduledAt: new Date(scheduledAt).toISOString() } : {}),
        priority,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : e instanceof Error ? e.message : 'Create failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New broadcast" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Template code">
          <input
            className="field-input"
            value={templateCode}
            onChange={(e) => setTemplateCode(e.target.value)}
            placeholder="e.g. c13.public_notice"
          />
        </Field>
        <Field label="Recipients — one personId/NIN per line">
          <textarea className="field-input font-mono text-xs" rows={5} value={personIds} onChange={(e) => setPersonIds(e.target.value)} />
        </Field>
        <Field label="Schedule for (optional — leave blank to keep as a draft you dispatch by hand)">
          <input className="field-input" type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={priority} onChange={(e) => setPriority(e.target.checked)} disabled={!canEmergency} />
          Emergency lane (bypasses quiet hours) — requires EMERGENCY_BROADCASTER
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !templateCode || !personIds}>
            {busy ? 'Creating…' : 'Create broadcast'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
